import React, { useRef, useState } from "react";
import { X, Upload, Sparkles, Download, Loader2, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function absoluteImg(url) {
  if (!url) return "";
  if (url.startsWith("http") || url.startsWith("data:")) return url;
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
}

export default function TryOnModal({ open, onClose, product, productImages }) {
  // productImages is optional — uses the currently selected color's images if provided
  const displayImage = (productImages?.length > 0 ? productImages : product?.images || [])[0];
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState("");
  const fileRef = useRef();

  if (!open) return null;

  const reset = () => {
    setPhotoFile(null);
    setPhotoPreview("");
    setResult("");
  };

  const close = () => {
    reset();
    onClose?.();
  };

  const onFile = (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Image files only"); return; }
    if (f.size > 8 * 1024 * 1024) { toast.error("Max 8 MB"); return; }
    setPhotoFile(f);
    setResult("");
    const reader = new FileReader();
    reader.onload = () => setPhotoPreview(reader.result);
    reader.readAsDataURL(f);
  };

  const generate = async () => {
    if (!photoFile) { toast.error("Upload your photo first"); return; }
    setBusy(true);
    setResult("");
    try {
      const reader = new FileReader();
      const b64 = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(photoFile);
      });
      const res = await api.post("/tryon", {
        person_base64: b64,
        product_id: product.id,
      }, { timeout: 120000 });
      setResult(res.data.image);
      toast.success("LOOKING SHARP");
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "";
      toast.error("GENERATION FAILED", { description: String(detail).slice(0, 120) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[60] flex items-center justify-center p-4 overflow-y-auto" onClick={close} data-testid="tryon-modal">
      <div className="bg-[#0A0A0A] border border-[#27272A] max-w-4xl w-full my-8" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-5 py-4 border-b border-[#27272A] flex justify-between items-center">
          <div>
            <p className="label-mono text-[#CFF048]">// AI TRY-ON</p>
            <h2 className="font-display font-black text-xl uppercase mt-0.5">See it on you</h2>
          </div>
          <button onClick={close} className="text-[#71717A] hover:text-white" data-testid="close-tryon"><X size={20} /></button>
        </div>

        <div className="grid md:grid-cols-2">
          {/* LEFT — input */}
          <div className="p-6 border-r border-[#27272A]">
            <p className="label-mono text-[#71717A] mb-2">// STEP 01 — YOUR PHOTO</p>
            <div
              className="dropzone p-6 text-center cursor-pointer min-h-[260px] flex items-center justify-center"
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("drag"); }}
              onDragLeave={(e) => e.currentTarget.classList.remove("drag")}
              onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove("drag"); onFile(e.dataTransfer.files[0]); }}
              data-testid="tryon-dropzone"
            >
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => onFile(e.target.files[0])} data-testid="tryon-file-input" />
              {photoPreview ? (
                <img src={photoPreview} alt="your photo" className="max-h-[300px] mx-auto" />
              ) : (
                <div>
                  <Upload size={36} className="text-[#CFF048] mx-auto" />
                  <p className="font-display font-bold mt-3">DROP A FULL-BODY PHOTO</p>
                  <p className="label-mono text-[#71717A] mt-1">Front-facing works best</p>
                </div>
              )}
            </div>

            <p className="label-mono text-[#71717A] mt-4 mb-2">// STEP 02 — PRODUCT</p>
            <div className="border border-[#27272A] p-3 flex items-center gap-3 bg-[#111]">
              <img src={absoluteImg(displayImage || product?.images?.[0])} alt="" className="w-16 h-16 object-cover border border-[#27272A]" />
              <div className="min-w-0">
                <p className="font-display font-bold truncate">{product?.name}</p>
                <p className="label-mono text-[#71717A]">{product?.collection?.toUpperCase()}</p>
              </div>
            </div>

            <button
              onClick={generate}
              disabled={busy || !photoFile}
              className="btn-neon w-full mt-5"
              data-testid="tryon-generate-btn"
            >
              {busy ? <><Loader2 size={16} className="animate-spin" /> GENERATING</> : <><Sparkles size={16} /> TRY IT ON</>}
            </button>

            <p className="label-mono text-[#71717A] mt-4 text-[10px] leading-relaxed">
              Your photo is sent only to our AI for this single render and is NOT stored on our servers.
              By using try-on you confirm this is a photo of yourself or one you have permission to use.
            </p>
          </div>

          {/* RIGHT — output */}
          <div className="p-6">
            <p className="label-mono text-[#CFF048] mb-2">// OUTPUT</p>
            <div className="border border-[#27272A] bg-black aspect-[3/4] flex items-center justify-center overflow-hidden" data-testid="tryon-output">
              {busy ? (
                <div className="text-center">
                  <p className="term-loader">RENDERING_YOU</p>
                  <p className="label-mono text-[#71717A] mt-3 text-[10px]">
                    {`>`}_ READING_POSE<br/>
                    {`>`}_ FITTING_GARMENT<br/>
                    {`>`}_ MATCHING_LIGHT
                  </p>
                </div>
              ) : result ? (
                <img src={result} alt="try-on" className="w-full h-full object-contain" />
              ) : (
                <p className="label-mono text-[#71717A]">// AWAITING_INPUT</p>
              )}
            </div>

            {result && (
              <div className="flex gap-2 mt-4">
                <a href={result} download={`cultlyf-tryon.png`} className="btn-outline flex-1" data-testid="tryon-download-btn">
                  <Download size={14} /> SAVE
                </a>
                <button onClick={reset} className="btn-outline flex-1" data-testid="tryon-retry-btn">
                  <RefreshCcw size={14} /> TRY AGAIN
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
